"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224016" className="flex gap-6">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-xs uppercase tracking-widest font-default font-medium text-foreground/70 hover:text-primary transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-4 pb-6 border-t border-border pt-6">
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-2 mt-4 pb-6 border-t border-border pt-6">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-sm uppercase tracking-widest font-default font-medium text-foreground/80 hover:text-primary block py-3 px-2 rounded-md hover:bg-secondary transition-colors" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
      <div className="pt-4 mt-2 border-t border-border/50 px-2">
        <Button variant="ghost" className="w-full justify-center uppercase text-xs tracking-widest font-default font-bold bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all duration-300 rounded-full shadow-md" onClick={onClose}>
          Book Now
        </Button>
      </div>
    </div>;
}
export default function PlatformHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-20">
          {}
          <Link to="/" className="flex-shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1635001/img/logo_platform_dark.png" alt="Coastal Cleaning of Vero Beach Logo" className="h-10 w-auto object-contain" />
          </Link>

          {}
          <div className="hidden md:flex items-center gap-8">
            <MenuProvider menu_id="41">
              <DesktopMenu />
            </MenuProvider>
            
            <div className="h-4 w-px bg-border/60" aria-hidden="true" />
            
            <Button variant="ghost" className="uppercase text-xs tracking-widest font-default font-bold bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all duration-300 px-6 rounded-full shadow-md">
              Book Now
            </Button>
          </div>

          {}
          <div className="md:hidden flex items-center">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-expanded={isMobileOpen} aria-label="Toggle navigation menu" className="hover:bg-secondary">
              {isMobileOpen ? <X className="size-5 text-foreground" /> : <Menu className="size-5 text-foreground" />}
            </Button>
          </div>
        </div>

        {}
        {isMobileOpen && <div className="md:hidden overflow-hidden animate-in slide-in-from-top-4 fade-in duration-200">
            <MenuProvider menu_id="41">
              <MobileMenu onClose={() => setIsMobileOpen(false)} />
            </MenuProvider>
          </div>}
      </div>
    </section>;
}